import React, { useMemo, useCallback, useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useFormContext, Controller } from "react-hook-form";
import { useDispatch } from "utils/redux";
import { api } from "services/api";
import KaboodleModal from "./KaboodleModal";
import Button from "components/Button/Button";

import { TextInput } from "components/inputs";

import { useCreateEmployerMutation } from "services/employer-api";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "components/Card/Card";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  name: string;
};

const CreateEmployerModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormData>();

  const [createEmployer, { isSuccess }] = useCreateEmployerMutation();

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  useEffect(() => {
    reset({});
  }, [reset]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await createEmployer({
          data: {
            name: data.name,
          },
        }).unwrap();

        if (result.id) {
          dispatch(api.util.invalidateTags(["Employers"]));
          reset();
          onClose();
          if (location.pathname.includes("employers"))
            navigate(`${location.pathname}/${result.id}/overview`);
        }
      } catch (err) {
        console.error("Failed to create client:", err);
      }
    },
    [createEmployer, reset, onClose, navigate, dispatch, location],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create Employer">
        <Controller
          control={control}
          name="name"
          rules={{ required: "Employer name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Name"
              label="Employer Name"
              errors={errors}
              required={true}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateEmployerModal;
